import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaInstagram } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { STAY_INFORMED_ITEMS, getStayInformedHref } from '../../constants/stayInformedLinks';

const FooterContainer = styled.footer`
  background-color: ${props => props.theme.navbarBg};
  color: ${props => props.theme.inverseText};
  padding: 2rem 0;
  margin-top: 2rem;
  transition: background-color 0.3s ease, color 0.3s ease;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: grid;
  align-items: start;
  gap: 1.5rem 1rem;

  /* Six sections on one row at desktop (SlickPicks, Account, Models, Resources, Tail us, Legal). */
  @media (min-width: 960px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    column-gap: 0.875rem;
    row-gap: 0;
  }

  @media (max-width: 959px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    column-gap: 1.25rem;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FooterTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.inverseText};
`;

const FooterLink = styled(Link)`
  color: ${props => props.theme.mutedText};
  text-decoration: none;
  margin-bottom: 0.5rem;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.primary};
  }
`;

const FooterExternalLink = styled.a`
  color: ${props => props.theme.mutedText};
  text-decoration: none;
  margin-bottom: 0.5rem;
  transition: color 0.2s ease;

  &:hover {
    color: ${props => props.theme.primary};
  }
`;

const FooterText = styled.span`
  color: ${props => props.theme.mutedText};
  margin-bottom: 0.5rem;
`;

const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 1rem 0;
  border-top: 1px solid ${props => props.theme.border};
  margin-top: 2rem;
  text-align: center;
  color: ${props => props.theme.mutedText};
  font-size: 0.875rem;
  transition: border-color 0.3s ease, color 0.3s ease;
`;

const VersionInfo = styled.div`
  margin-top: 0.5rem;
  color: ${props => props.theme.mutedText};
  font-size: 0.75rem;
`;

const Footer = () => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();

  return (
    <FooterContainer theme={theme}>
      <FooterContent>
        <FooterSection>
          <FooterTitle theme={theme}>SlickPicks</FooterTitle>
          <FooterLink theme={theme} to="/why-us">Why Us?</FooterLink>
          <FooterLink theme={theme} to="/how-it-works">How It Works</FooterLink>
          <FooterLink theme={theme} to="/about">About Us</FooterLink>
          <FooterLink theme={theme} to="/contact">Contact Us</FooterLink>
        </FooterSection>

        <FooterSection>
          <FooterTitle theme={theme}>Account</FooterTitle>
          <FooterLink theme={theme} to="/register">Create Account</FooterLink>
          <FooterLink theme={theme} to="/login">Login</FooterLink>
          <FooterLink theme={theme} to="/dashboard">Dashboard</FooterLink>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle theme={theme}>Models</FooterTitle>
          <FooterLink theme={theme} to="/game-picks">Game Picks</FooterLink>
          <FooterLink theme={theme} to="/model-stats">Model Stats</FooterLink>
          <FooterLink theme={theme} to="/mlb/total-bases">MLB Total Bases</FooterLink>
          <FooterLink theme={theme} to="/mlb/hit">MLB Hit</FooterLink>
          <FooterLink theme={theme} to="/mlb/two-hits">MLB 2+ Hits</FooterLink>
          <FooterLink theme={theme} to="/mlb/hr">MLB Home Runs</FooterLink>
        </FooterSection>

        <FooterSection>
          <FooterTitle theme={theme}>Resources</FooterTitle>
          <FooterLink theme={theme} to="/model-stats">Model Stats</FooterLink>
          <FooterLink theme={theme} to="/resources/odds-calculator">Odds Calculator</FooterLink>
          <FooterLink theme={theme} to="/resources/parlay-calculator">Parlay Calculator</FooterLink>
          <FooterLink theme={theme} to="/resources/betting-strategies">Betting Strategies</FooterLink>
        </FooterSection>

        <FooterSection>
          <FooterTitle theme={theme}>Tail us</FooterTitle>
          {STAY_INFORMED_ITEMS.map((item) => {
            const href = getStayInformedHref(item, isAuthenticated);
            return item.external ? (
              <FooterExternalLink
                key={item.key}
                theme={theme}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={item.subtitle}
              >
                {item.title}
              </FooterExternalLink>
            ) : (
              <FooterLink key={item.key} theme={theme} to={href} title={item.subtitle}>
                {item.title}
              </FooterLink>
            );
          })}
        </FooterSection>
        
        <FooterSection>
          <FooterTitle theme={theme}>Legal</FooterTitle>
          <FooterLink theme={theme} to="/terms">Terms of Service</FooterLink>
          <FooterLink theme={theme} to="/privacy">Privacy Policy</FooterLink>
          <FooterText theme={theme}>1-800-GAMBLER</FooterText>
        </FooterSection>
      </FooterContent>
      
      <FooterBottom theme={theme}>
        <p>
          &copy; {new Date().getFullYear()} SlickPicks. All rights reserved.
          <a
            href="https://www.instagram.com/slickpicksbet/"
            target="_blank"
            rel="noopener noreferrer"
            title="Instagram"
            style={{ marginLeft: 12, color: '#4299e1', verticalAlign: 'middle' }}
          >
            <FaInstagram size={20} />
          </a>
        </p>
        <VersionInfo theme={theme}>
          Version: {process.env.REACT_APP_VERSION || 'unknown'} | Built: {process.env.REACT_APP_BUILD_DATE || 'unknown'}
        </VersionInfo>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer; 